// Iter 306 — staff panel on the partner commissions page: every inquiry the
// partner raised, with reply / resolve / re-issue. Deep link ?inquiry=<id>.
import { useEffect, useState } from "react";
import { MessageSquareText } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { InquiryThread } from "./InquiryThread";
import { ReissueDialog } from "./ReissueDialog";

export function InquiriesPanel({ partnerKey, focusId, onReissued }) {
  const [items, setItems] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [reissue, setReissue] = useState(null);     // { period, inquiry }
  const load = () => api.get(`/commissions/partners/${encodeURIComponent(partnerKey)}/inquiries`)
    .then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, [partnerKey]);
  useEffect(() => {
    if (focusId) document.getElementById(`inquiry-anchor-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusId, items.length]);
  const visible = items.filter((i) => showResolved || i.status !== "resolved" || i.inquiry_id === focusId);
  if (!items.length) return null;
  const openCount = items.filter((i) => i.status !== "resolved").length;
  return (
    <Card className="border-slate-200 shadow-sm p-3 mb-4" data-testid="inquiries-panel">
      <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-wider font-medium text-slate-500">
        <MessageSquareText className="h-3.5 w-3.5" /> Partner inquiries
        {openCount > 0 && <span className="rounded-full bg-amber-100 px-1.5 text-amber-800" data-testid="inquiries-open-count">{openCount} open</span>}
        <button type="button" className="ml-auto text-[11px] normal-case tracking-normal text-primary hover:underline"
                onClick={() => setShowResolved((v) => !v)} data-testid="inquiries-toggle-resolved">
          {showResolved ? "Hide resolved" : `Show resolved (${items.length - openCount})`}
        </button>
      </div>
      <div className="space-y-2">
        {visible.map((inq) => (
          <div key={inq.inquiry_id} id={`inquiry-anchor-${inq.inquiry_id}`} className={inq.inquiry_id === focusId ? "ring-2 ring-primary/40 rounded-lg" : ""}>
            <InquiryThread inquiry={inq} isStaff
                           onChange={(fresh) => setItems((xs) => xs.map((x) => (x.inquiry_id === fresh.inquiry_id ? fresh : x)))}
                           onReissue={(i) => setReissue({ period: i.period_label, inquiry: i })} />
          </div>
        ))}
      </div>
      {reissue && (
        <ReissueDialog partnerKey={partnerKey} period={reissue.period} inquiry={reissue.inquiry}
                       onClose={() => setReissue(null)} onDone={(issue) => { load(); onReissued?.(issue); }} />
      )}
    </Card>
  );
}
